import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { PlusIcon, PencilIcon, TrashIcon, EyeIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { businessApi } from '../services/api';
import { Business } from '../types';
import { Modal } from '../components/Modal';

const businessSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  address: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().email('Invalid email').optional().or(z.literal('')),
  website: z.string().optional(),
});

type BusinessForm = z.infer<typeof businessSchema>;

export function Businesses() {
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBusiness, setEditingBusiness] = useState<Business | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<BusinessForm>({
    resolver: zodResolver(businessSchema),
  });

  useEffect(() => {
    fetchBusinesses();
  }, []);

  const fetchBusinesses = async () => {
    try {
      const response = await businessApi.getMyBusinesses();
      setBusinesses(response.data.data.businesses || []);
    } catch (error) {
      toast.error('Failed to load businesses');
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (data: BusinessForm) => {
    try {
      if (editingBusiness) {
        await businessApi.updateBusiness(editingBusiness.id, data);
        toast.success('Business updated successfully');
      } else {
        await businessApi.createBusiness(data);
        toast.success('Business created successfully');
      }
      setIsModalOpen(false);
      setEditingBusiness(null);
      reset();
      fetchBusinesses();
    } catch (error) {
      toast.error(editingBusiness ? 'Failed to update business' : 'Failed to create business');
    }
  };

  const handleEdit = (business: Business) => {
    setEditingBusiness(business);
    reset(business);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this business?')) return;
    
    try {
      await businessApi.deleteBusiness(id);
      toast.success('Business deleted successfully');
      fetchBusinesses();
    } catch (error) {
      toast.error('Failed to delete business');
    }
  };

  const handleAddNew = () => {
    setEditingBusiness(null);
    reset({
      name: '',
      description: '',
      address: '',
      phone: '',
      email: '',
      website: '',
    });
    setIsModalOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-secondary-900">My Businesses</h1>
        <button onClick={handleAddNew} className="btn-primary">
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Business
        </button>
      </div>

      {businesses.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {businesses.map((business) => (
            <div key={business.id} className="card p-6">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-secondary-900">{business.name}</h3>
                  <p className="text-sm text-secondary-500 mt-1">{business.address}</p>
                </div>
                <span
                  className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    business.isActive
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {business.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>

              <div className="mt-4 grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-lg font-semibold text-secondary-900">
                    {business._count?.services || 0}
                  </p>
                  <p className="text-xs text-secondary-500">Services</p>
                </div>
                <div>
                  <p className="text-lg font-semibold text-secondary-900">
                    {business._count?.staff || 0}
                  </p>
                  <p className="text-xs text-secondary-500">Staff</p>
                </div>
                <div>
                  <p className="text-lg font-semibold text-secondary-900">
                    {business._count?.bookings || 0}
                  </p>
                  <p className="text-xs text-secondary-500">Bookings</p>
                </div>
              </div>

              <div className="mt-6 flex space-x-2">
                <a
                  href={`/businesses/${business.id}`}
                  className="flex-1 btn-secondary text-center text-sm"
                >
                  <EyeIcon className="h-4 w-4 mr-1 inline" />
                  View
                </a>
                <button
                  onClick={() => handleEdit(business)}
                  className="flex-1 btn-secondary text-sm"
                >
                  <PencilIcon className="h-4 w-4 mr-1 inline" />
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(business.id)}
                  className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card p-12 text-center">
          <p className="text-secondary-500 mb-4">You don't have any businesses yet.</p>
          <button onClick={handleAddNew} className="btn-primary">
            <PlusIcon className="h-5 w-5 mr-2" />
            Create Your First Business
          </button>
        </div>
      )}

      {/* Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingBusiness(null);
        }}
        title={editingBusiness ? 'Edit Business' : 'Add New Business'}
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="label">Business Name *</label>
            <input {...register('name')} type="text" className="input" />
            {errors.name && <p className="text-sm text-red-600 mt-1">{errors.name.message}</p>}
          </div>

          <div>
            <label className="label">Description</label>
            <textarea {...register('description')} className="input" rows={3} />
          </div>

          <div>
            <label className="label">Address</label>
            <input {...register('address')} type="text" className="input" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Phone</label>
              <input {...register('phone')} type="tel" className="input" />
            </div>
            <div>
              <label className="label">Email</label>
              <input {...register('email')} type="email" className="input" />
              {errors.email && <p className="text-sm text-red-600 mt-1">{errors.email.message}</p>}
            </div>
          </div>

          <div>
            <label className="label">Website</label>
            <input {...register('website')} type="url" className="input" placeholder="https://" />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={() => {
                setIsModalOpen(false);
                setEditingBusiness(null);
              }}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting} className="btn-primary">
              {isSubmitting ? 'Saving...' : editingBusiness ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
